import { h, Component } from 'preact';
import Store from 'store/store';
import Calendar from 'components/bolt/icons/calendar';
import Clock from 'components/bolt/icons/clock';
import Reel from 'components/bolt/icons/reel';
import Link from 'components/bolt/link/link';
import Close from 'components/bolt/icons/close';

import { accessNested, joinClasses } from 'utils/utils';
import { prioritiseTicketProviders, convertTimeTo12Hr, getShowtimesData } from 'utils/bolt/showtimes';
import formatDateTime from 'utils/formatDateTime';
import s from 'components/bolt/showtimes/providerInterstitial.sss';

const PROVIDERS = {
	movietickets: 'MovieTickets'
};

export default class ProviderInterstitial extends Component {
	constructor(props) {
		super(props);

		this.onClose = this.onClose.bind(this);
		this.onShadowClick = this.onShadowClick.bind(this);

		this.links = props.selectedScreening.link;
		this.providers = prioritiseTicketProviders(Object.keys(this.links));
	}

	async onClose() {
		this.setState({ closing: true });
		await new Promise(res => setTimeout(res, 500));
		Store.emit(this.props.isPreviewPanel ? 'CLOSE_PREVIEW_PANEL_PROVIDER_INTERSTITIAL' : 'CLOSE_PROVIDER_INTERSTITIAL');
		this.setState({ closing: false });
	}

	onShadowClick(e) {
		if (e?.target === this.$shadow) {
			this.onClose();
		}
	}

	trackConversion(url, provider) {
		const { trackConversion, selectedTheater, selectedScreening, mode } = this.props;
		trackConversion?.(selectedTheater, selectedScreening, provider, mode, url);
	}

	renderHeader() {
		const { copy } = this.props;
		const { appData, client } = Store.get();
		const whereToByCopy = copy?.baseCopy?.$WHERE_TO_BUY || 'Where to buy?';
		const closeAriaLabel = copy?.accessibilityCopy?.$BACK_BTN_SHOWTIMES_ARIA || 'Return to previous view';
		const fontStyles = appData.styling?.fontVariables;
		const isDesktop = client.isDesktopSize;

		return (
			<div class={s.header}>
				{isDesktop && (
					<svg class={s.cutout} viewBox="0 0 10 100" version="1.1" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M10,0 L10,100 L0,100 L0.000674999097,61.3169386 C4.66109189,59.6694024 8,55.2246231 8,50 C8,44.7753769 4.66109189,40.3305976 0.000674999097,38.6830614 L0,0 L10,0 Z"
							fill-rule="evenodd"
						/>
					</svg>
				)}
				<div class={s.headerContent}>
					<h4 class={joinClasses(fontStyles?.providerInterstitialTitle, s.title)}>{whereToByCopy}</h4>
					<Close class={s.close} onClick={this.onClose} role='button' ariaLabel={closeAriaLabel} />
				</div>
			</div>
		);
	}

	renderTheaterLogo() {
		const logoAlt = accessNested(Store.get(), 'appData.copy.accessibilityCopy.$CINEMA_LOGO_ALT', '<chain>');
		const logo = accessNested(this.props, 'selectedTheater.chain.logo') || 'default.png';
		const logoLink = `${this.props.sharedImageRoot}/logos/${this.props.logoShade}/${logo}`;
		const strippedChainName = this.props.selectedTheater.chain && this.props.selectedTheater.chain.name.replace(/\..*/, '');

		return (
			<div class={s.theaterLogo}>
				<img src={logoLink} alt={logoAlt.replace('<chain>', strippedChainName)} />
			</div>
		);
	}

	renderScreeningDetails() {
		const { selectedScreening, copy, selectedTheater } = this.props;
		const theaterName = selectedTheater.name || 'Cinema';
		let { date, time, format, attributes } = selectedScreening;
		const { appData } = Store.get();
		const fontStyles = appData.styling?.fontVariables;

		const dateParts = date.split('-');
		let screeningDate = new Date(+dateParts[0], dateParts[1] - 1, +dateParts[2]);
		screeningDate = formatDateTime(screeningDate, 'ddd dd MMM');

		const screeningAttributes = [...attributes];
		// const screeningAttributes = ['General Admission', 'Closed Caption', 'Reserved Seating'];

		const attributesCopy = copy?.baseCopy?.filters?.attributes || {};

		if ((appData.pages?.boltShowtimes?.data?.clockFormat || '24') === '12') {
			time = convertTimeTo12Hr(time);
		}

		return (
			<div class={s.screeningDetails}>
				<div class={joinClasses(fontStyles.providerInterstitialTheaterName, s.theaterName)}>
					<span>{theaterName}</span>
				</div>
				<div class={s.mainDetails}>
					<div class={s.date}>
						<Calendar ariaLabel="Calendar" />
						<span class={fontStyles.providerDateTimeFormat}>{screeningDate}</span>
					</div>
					<div class={s.time}>
						<Clock ariaLabel="Clock" />
						<span class={fontStyles.providerDateTimeFormat}>{time}</span>
					</div>
					<div class={s.format}>
						<Reel ariaLabel="Reel" />
						<span class={fontStyles.providerDateTimeFormat}>{format || '2D'}</span>
					</div>
				</div>
				<div class={joinClasses(s.attributes, fontStyles.providerInterstitialAttribute)}>
					{screeningAttributes.map(attribute => (
						<span>{attributesCopy[attribute] || attribute}</span>
					))}
				</div>
			</div>
		);
	}

	renderProviderOptions() {
		const showtimesAppData = getShowtimesData();
		const highlighted = showtimesAppData?.providers?.highlightedTicketProvider;

		return (
			<div class={joinClasses(s.providers, highlighted && s.hasHighlightedProvider)}>
				{this.providers.map(provider => this.renderProvider(provider))}
			</div>
		);
	}

	renderProvider(provider) {
		const { sharedImageRoot, logoShade, copy, selectedTheater } = this.props;
		const { appData } = Store.get();
		const theaterLogo = selectedTheater?.chain?.logo || 'default.png';
		const theaterLogoLink = `${sharedImageRoot}/logos/${logoShade}/${theaterLogo}`;
		const fontStyles = appData.styling?.fontVariables;
		const showtimesAppData = getShowtimesData();
		const highlighted = showtimesAppData?.providers?.highlightedTicketProvider;
		const isHighlighted = provider === highlighted;
		const isNonHighlighted = highlighted && !isHighlighted;

		const imgSrc = provider === 'direct' ? theaterLogoLink : `${sharedImageRoot}/logos/ticketproviders/${logoShade}/${provider}.png`;
		const name = provider === 'direct' ? (copy.baseCopy.$DIRECT_FROM_CINEMA || 'Direct from cinema') : PROVIDERS[provider] || (provider.charAt(0).toUpperCase() + provider.slice(1));
		const providerAriaLabel = copy.accessibilityCopy.$PROVIDER_OPTION_ARIA.replace(provider === 'direct' ? selectedTheater.name : provider);
		const linkCopy = copy.baseCopy.$GO_TO_SITE || 'Go to Site';

		return (
			<div 
				class={joinClasses(
					s.provider,
					fontStyles.providerDirect,
					isHighlighted && s.isHighlighted,
					isNonHighlighted && s.isNonHighlighted,
					s[logoShade]
				)}
				ariaLabel={providerAriaLabel}
			>
				<div class={s.providerLogo}>
					<img src={imgSrc} />
				</div>
				<div class={joinClasses(s.providerName, fontStyles.providerName)}>{name}</div>
				<Link
					class={joinClasses(s.providerLink, fontStyles.providerLink)}
					target="_blank"
					href={this.links[provider]}
					onClick={() => this.trackConversion(this.links[provider], provider)}
					ariaLabel={providerAriaLabel}
					rel="noopener noreferrer"
				>
					{linkCopy}
				</Link>
			</div>
		);
	}

	render(props, state) {
		const { closing } = state;

		return (
			<div class={joinClasses(s.wrapper, closing && s.closing)}>
				<div class={s.shadow} ref={ref => this.$shadow = ref} onClick={this.onShadowClick}/>
				<div class={s.container}>
					<div class={s.scroller}>
						<div class={s.content}>
							{this.renderHeader()}
							{this.renderScreeningDetails()}
							{this.renderProviderOptions()}
						</div>
					</div>
				</div>
			</div>
		);
	}
}
